import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InlineHint } from "../../../components/UIToolkit/molecules/InlineHint";
import { MIN_CUT_HOURS_PER_DAY } from "./recMath.utils";
import { CUT_LIMIT_COPY_VALUES } from "./recommendationRow.utils";
import type { RecommendationId } from "./recommendations.constants";

type Props = {
	recId: RecommendationId;
	// The slider sits on a cut too small to commit, so Add is disabled — say why, inline.
	isUncommittableCut: boolean;
	// The lever's whole cut limit, in hours a day: under one hour no drag can ever reach a committable cut.
	maxCutHoursPerDay: number;
	// The slider sits on its cut floor; `cutLimitKey` names the rule that stops it (null: no limit sent).
	atCutFloor: boolean;
	cutLimitKey: string | null;
	// An RN lever is being cut: the federal RN-on-duty warning.
	showRnWarning: boolean;
};

/**
 * The full-width notes under a lever card: why Add is disabled, which rule stops the cut side, and the
 * federal RN-floor warning. Inline (not tooltips), so they read on touch and with a keyboard as well as
 * with a pointer, and never cover the readout above the slider. Each animates in and out on its own.
 */
export const RecommendationRowNotes: FC<Props> = ({ recId, isUncommittableCut, maxCutHoursPerDay, atCutFloor, cutLimitKey, showRnWarning }) => {
	const { t } = useTranslation();
	// When the lever's whole limit is under an hour, "drag further" is a dead end — say the cut can't be added.
	const addDisabledKey =
		maxCutHoursPerDay < MIN_CUT_HOURS_PER_DAY ? "pbj_model.recommendations.add_disabled_limit_under_hour" : "pbj_model.recommendations.add_disabled_small_cut";
	return (
		<>
			<InlineHint open={isUncommittableCut} tone="info" dataTestId={`pbj-model-recommendation-add-disabled-${recId}`} sx={{ mt: 1.5 }}>
				{t(addDisabledKey)}
			</InlineHint>
			<InlineHint open={atCutFloor && cutLimitKey !== null} tone="info" dataTestId={`pbj-model-recommendation-cut-limit-${recId}`} sx={{ mt: 1.5 }}>
				{cutLimitKey ? t(cutLimitKey, CUT_LIMIT_COPY_VALUES) : ""}
			</InlineHint>
			{/* Federal RN-on-duty rule: the cut limit only guarantees the daily AVERAGE stays at 8 RN
			    hours, so a cut on any RN lever carries this warning while it's below 0. */}
			<InlineHint
				open={showRnWarning}
				tone="warning"
				icon={<WarningAmberRoundedIcon />}
				dataTestId={`pbj-model-recommendation-rn-warning-${recId}`}
				sx={{ mt: 1.5 }}
			>
				{t("pbj_model.recommendations.rn_floor_warning")}
			</InlineHint>
		</>
	);
};
